import { useEffect, useEffectEvent, useState } from 'react';

/**
 * A value read off the clock, re-read on a fixed interval: the component
 * re-renders only when the value READ changes (`Object.is`), not on every
 * tick. A day-grained reading ("the site's today") re-renders once a day
 * instead of once a minute; a `Date` reading changes every tick. `read` is
 * applied to the latest tick at render, so a change in what it closes over
 * (e.g. the site's zone arriving) shows on that render, not at the next tick.
 * Local clock only.
 *
 * @param intervalMs how often to re-read (default 30s).
 */
export function useClockValue<T>(read: (now: Date) => T, intervalMs = 30_000): T {
  const [now, setNow] = useState(() => new Date());
  const readLatest = useEffectEvent(read);
  useEffect(() => {
    let last = readLatest(new Date());
    const intervalId = setInterval(() => {
      const next = new Date();
      const value = readLatest(next);
      if (Object.is(value, last)) return;
      last = value;
      setNow(next);
    }, intervalMs);
    return () => clearInterval(intervalId);
  }, [intervalMs]);
  return read(now);
}

const asIs = (now: Date): Date => now;

/**
 * A `Date` that re-renders the component on a fixed interval, so relative-time
 * UI (e.g. "Last synced 2 minutes ago", live workforce timestamps) stays current
 * without any network calls. Local clock only.
 *
 * @param intervalMs how often to tick (default 30s).
 */
export function useNow(intervalMs = 30_000): Date {
  return useClockValue(asIs, intervalMs);
}
